/**
 * Built-in placeholder assets for missing-asset fallback paths.
 *
 * createMissingPatternImage() produces a visually unmistakable "broken link"
 * tile - red diagonal stripes on a grey checkerboard at 32×32 pixels.
 * Used by resolveRegionMethods when a Family-B patternRefId resolves to
 * no entry in assetData.
 *
 * Stamp mask missing assets do NOT need a placeholder ImageData because the
 * pipeline fallback is a built-in pixel stamp (StampShape { type: 'pixel' }),
 * which carries no binary payload. See resolveRegionMethods in state.ts for
 * the stamp-mask fallback path.
 *
 * Both functions are pure: no DOM globals, no side effects, no imports beyond
 * the standard ImageData constructor (polyfilled in tests via tests/setup.ts).
 */

/** Size of the generated missing-pattern tile in pixels. */
const TILE_SIZE = 32;

/** Checkerboard square size in pixels. */
const CHECK_SIZE = 8;

/** Diagonal stripe width in cells (1 = one square wide). */
const STRIPE_STEP = CHECK_SIZE * 2;

/**
 * Returns a 32×32 ImageData with red diagonal stripes on a grey
 * checkerboard. Produced entirely from RGBA arithmetic so there are no
 * external assets or DOM dependencies.
 *
 * Visual language:
 *   - Light grey / mid-grey checkerboard as the base.
 *   - Bright-red stripes at 45° cover cells where (x + y) / STRIPE_STEP is
 *     odd, making the pattern unmistakably "error" at a glance without
 *     being so busy that it obscures the region outline.
 */
export function createMissingPatternImage(): ImageData {
  const size = TILE_SIZE;
  const data = new Uint8ClampedArray(size * size * 4);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;

      // Checkerboard: two alternating grey tones.
      const checkX = Math.floor(x / CHECK_SIZE);
      const checkY = Math.floor(y / CHECK_SIZE);
      const isLightCheck = (checkX + checkY) % 2 === 0;
      const grey = isLightCheck ? 200 : 140;

      // Diagonal stripe: stripe runs at 45° so (x + y) determines the
      // stripe band. An odd stripe-step index paints red.
      const stripeIndex = Math.floor((x + y) / STRIPE_STEP);
      const isRedStripe = stripeIndex % 2 !== 0;

      if (isRedStripe) {
        data[i] = 220;     // R - a saturated but not garish red
        data[i + 1] = 30;  // G
        data[i + 2] = 30;  // B
      } else {
        data[i] = grey;
        data[i + 1] = grey;
        data[i + 2] = grey;
      }
      data[i + 3] = 255; // alpha - fully opaque
    }
  }

  return new ImageData(data, size, size);
}
